/**
 * Auth Service
 * Handles all authentication-related API calls
 */

import { authApiClient } from './apiClient';

const AUTH_ENDPOINTS = {
  REGISTER: '/register',
  SEND_OTP: '/otp/request',
  VERIFY_OTP: '/otp/verify',
  SOCIAL_GOOGLE: '/social/google',
  SOCIAL_APPLE: '/social/apple',
};

const USER_NOT_FOUND_KEY = 'user_not_found';

export const authService = {
  /**
   * Request OTP via email for login
   * @param {string} email
   * @param {string} role
   */
  async loginWithEmail(email, role) {
        const payload = { email };
    if (role) payload.role = role;

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.SEND_OTP, payload);

    if (!result.success) {
      if (result.raw?.key === USER_NOT_FOUND_KEY) {
        return {
          success: false,
          data: null,
          key: result.raw.key,
          message: 'User not found. Please register first.',
        };
      }

      // ⚠️ THE KEY IS CARRIED OUT, not just the sentence. A code now goes to ONE channel,
      // so `otp_channel_unavailable` means "there is nothing to send to on that channel" —
      // a dead end the passenger escapes only by switching identifier. With `message`
      // alone the screen could do nothing but repeat the backend's prose and leave them
      // retrying the same field.
      return {
        success: false,
        data: null,
        key: result.raw?.key || null,
        message: result.error || 'Login failed. Please try again.',
      };
    }

    return {
      success: true,
      data: result.data,
      // Where the backend actually SENT it ('email' | 'sms') — since Sept 2026 the single
      // channel named by the request. Carried out so the OTP screen's sentence comes from
      // the server's answer rather than from its own tab state.
      channel: result.data?.channel || null,
      key: null,
      message: result.data?.message || 'OTP sent successfully',
    };
  },

  /**
   * Request OTP via phone for login
   * @param {string} phone
   * @param {string} role
   */
  async loginWithPhone(phone, role) {
        const payload = { phone };
    if (role) payload.role = role;

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.SEND_OTP, payload);

    if (!result.success) {
      if (result.raw?.key === USER_NOT_FOUND_KEY) {
        return {
          success: false,
          data: null,
          key: result.raw.key,
          message: 'User not found. Please register first.',
        };
      }

      // ⚠️ THE KEY IS CARRIED OUT, not just the sentence. A code now goes to ONE channel,
      // so `otp_channel_unavailable` means "there is nothing to send to on that channel" —
      // a dead end the passenger escapes only by switching identifier. With `message`
      // alone the screen could do nothing but repeat the backend's prose and leave them
      // retrying the same field.
      return {
        success: false,
        data: null,
        key: result.raw?.key || null,
        message: result.error || 'Login failed. Please try again.',
      };
    }

    return {
      success: true,
      data: result.data,
      // Where the backend actually SENT it ('email' | 'sms') — since Sept 2026 the single
      // channel named by the request. Carried out so the OTP screen's sentence comes from
      // the server's answer rather than from its own tab state.
      channel: result.data?.channel || null,
      key: null,
      message: result.data?.message || 'OTP sent successfully',
    };
  },

  /**
   * Verify OTP code
   * @param {string} identifier - User email or phone
   * @param {string} otp - OTP code
   * @param {string} type - 'email' or 'phone'
   */
  async verifyOtp(identifier, otp, type = 'email') {
    
    const payload = { otp };
    if (type === 'phone') {
      payload.phone = identifier;
    } else {
      payload.email = identifier;
    }

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.VERIFY_OTP, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        message: result.error || 'OTP verification failed',
      };
    }

    return {
      success: true,
      data: result.data,
      message: 'OTP verified successfully',
    };
  },

  /**
   * Resend OTP
   * @param {string} email
   * @param {string} phone
   */
  async resendOtp(email, phone) {
    // ⚠️ EXACTLY ONE OF `email` / `phone` IS SENT. The identifier IS the channel now, so a
    // body carrying both is a request with no single answer: the caller would be asking
    // the backend to pick, and whichever it picked, the screen's "we sent a code to your…"
    // could be naming the other. The signature takes both and used to forward both, which
    // made that easy to do by accident — so it is now reported rather than sent.
    if (email && phone) {
      console.warn('[hopn] resendOtp called with BOTH email and phone — the code goes to one channel; send only the one it was requested with');
      return {success: false, data: null, key: null, channel: null, message: 'Could not resend the code. Please try again.'};
    }

        const payload = {};

    if (email) payload.email = email;
    if (phone) payload.phone = phone;

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.SEND_OTP, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        // A resend can hit `otp_channel_unavailable` exactly as a first send can.
        key: result.raw?.key || null,
        message: result.error || 'Failed to resend OTP',
      };
    }

    return {
      success: true,
      data: result.data,
      channel: result.data?.channel || null,
      key: null,
      message: 'OTP sent successfully',
    };
  },

  /**
   * Register a new user
   * @param {Object} userData
   */
  async register(userData) {
    
    const payload = {
      first_name: userData.firstName?.trim(),
      last_name: userData.lastName?.trim(),
      email: userData.email?.trim(),
      phone: userData.phone?.trim(),
    };

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.REGISTER, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        message: result.error || 'Registration failed',
      };
    }

    return {
      success: true,
      data: result.data,
      message: 'Registration successful',
    };
  },

  /**
   * Social login with Google
   * @param {string} idToken - Google id_token
   * @param {string} role
   */
  async loginWithGoogle(idToken, role) {
        const payload = { token: idToken };
    if (role) payload.role = role;

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.SOCIAL_GOOGLE, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        message: result.error || 'Google login failed. Please try again.',
      };
    }

    return {
      success: true,
      data: result.data,
      message: 'Login successful',
    };
  },

  /**
   * Social login with Apple
   *
   * The backend verifies the identity token itself, so the token is the whole
   * integration — no authorization code, no redirect/callback route.
   *
   * @param {string} idToken - Apple id_token
   * @param {string} role
   * @param {{firstName?: string, lastName?: string}} [name] - Only ever populated on
   *   a user's FIRST Apple authorization; Apple never sends it again, so forwarding
   *   it then is the one chance to create the account with a name on it.
   */
  async loginWithApple(idToken, role, name = {}) {
    const payload = { token: idToken };
    if (role) payload.role = role;
    if (name.firstName) payload.first_name = name.firstName;
    if (name.lastName) payload.last_name = name.lastName;

    const result = await authApiClient.postJson(AUTH_ENDPOINTS.SOCIAL_APPLE, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        message: result.error || 'Apple login failed. Please try again.',
      };
    }

    return {
      success: true,
      data: result.data,
      message: 'Login successful',
    };
  },
};

export default authService;
